import { useOutletContext } from 'react-router';
import { Button, Card, NotificationCard } from '@sighthost/ui';
import type { Domain, DomainContact } from '@/domains/domains-registry';

export default function DomainContactsTab() {
  const domain = useOutletContext<Domain>();

  return (
    <div className="space-y-6">
      {domain.registrant ? (
        <ContactCard
          role="Registrant"
          contact={domain.registrant}
          description="The legal owner of this domain. Changes here can trigger a 60-day registry lock."
        />
      ) : (
        <NotificationCard
          tone="info"
          title="Registrant details not loaded"
          body="Refresh the page to re-fetch registry contacts. If the issue persists, contact support."
        />
      )}

      <Card interactive={false} className="border-dashed bg-card-subtle p-6 text-sm text-foreground-soft">
        Admin, technical and billing contacts default to the registrant unless explicitly overridden.
        Per-role overrides and contact-edit flows ship in Phase 8e.9.
      </Card>
    </div>
  );
}

function ContactCard({
  role,
  contact,
  description,
}: {
  role: string;
  contact: DomainContact;
  description: string;
}) {
  return (
    <Card interactive={false}>
      <header className="flex items-center justify-between border-b border-border px-6 py-3">
        <div>
          <h3 className="text-sm font-semibold text-ink">{role}</h3>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
        <Button variant="outline" size="sm" disabled title="Phase 8e.9">
          Edit
        </Button>
      </header>

      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 px-6 py-5 sm:grid-cols-2">
        <Row label="Full name"     value={contact.fullName} />
        {contact.organisation && <Row label="Organisation" value={contact.organisation} />}
        <Row label="Email"          value={contact.email} />
        <Row label="Phone"          value={contact.phone} />
        <Row
          label="Address"
          spanFull
          value={`${contact.addressLine1}, ${contact.city}, ${contact.region} ${contact.postalCode}, ${contact.countryCode}`}
        />
      </dl>
    </Card>
  );
}

function Row({
  label,
  value,
  spanFull,
}: {
  label: string;
  value: React.ReactNode;
  spanFull?: boolean;
}) {
  return (
    <div className={spanFull ? 'sm:col-span-2' : undefined}>
      <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm text-ink">{value}</dd>
    </div>
  );
}
